import {type Draft, produce} from 'immer';
import {setWith, unset} from 'lodash';

type ControlsByClass<TControl> = {[classOrAll: string]: TControl};

type TogglesConfig<TControl> = {
  toggles?: {
    [mediaKey: string]: {[overlayKey: string]: ControlsByClass<TControl>};
  };
};

/**
 * Strip child `disabled` overrides when the parent "all" toggle changes, so
 * classes re-inherit visibility from the parent. Entries that still hold a
 * customization (per `hasCustomization`) are kept with `disabled` removed;
 * entries that would otherwise be empty are removed entirely.
 */
export function clearClassDisabledOverrides<TControl>(
  toggles: {[classOrAll: string]: TControl | Draft<TControl>},
  hasCustomization: (entry: TControl) => boolean
): void {
  for (const key of Object.keys(toggles)) {
    if (key === 'all') {
      continue;
    }

    const entry = toggles[key];
    if (hasCustomization(entry as TControl)) {
      unset(entry as object, 'disabled');
    } else {
      unset(toggles as object, key);
    }
  }
}

/**
 * Pure function: apply an overlay toggle update and return the new config.
 * When updating the overlay "all" control, removes child `disabled` overrides
 * so classes inherit visibility from the parent while preserving any
 * customization (color/opacity) they carry.
 *
 * `configPath` is `['toggles', mediaKey, overlayKey, classOrAll]`.
 */
export function applyOverlayToggleUpdate<
  TControl,
  TConfig extends TogglesConfig<TControl>,
>(
  config: TConfig | undefined,
  configPath: Array<string>,
  currentControl: Partial<TControl> | undefined,
  newControl: Partial<TControl>,
  hasCustomization: (entry: TControl) => boolean
): TConfig {
  return produce(config ?? ({} as TConfig), draft => {
    const isAllUpdate =
      configPath[0] === 'toggles' &&
      configPath[configPath.length - 1] === 'all';
    if (isAllUpdate) {
      const [, mediaKey, overlayKey] = configPath;
      const toggles = draft.toggles?.[mediaKey]?.[overlayKey];
      if (toggles != null) {
        clearClassDisabledOverrides(toggles, hasCustomization);
      }
    }

    return setWith(
      draft,
      configPath,
      {...currentControl, ...newControl},
      Object
    );
  });
}
